import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { useNotification } from '@/_shared/hooks/useNotification';
import { deleteContentsFavoritesCall, putContentsFavoritesCall } from '../state/api';

// Simple event emitter for favorite state synchronization
type FavoriteChangeListener = (contentId: string, isFavorite: boolean) => void;
const listeners = new Set<FavoriteChangeListener>();

const emitFavoriteChange = (contentId: string, isFavorite: boolean) => {
  listeners.forEach(listener => listener(contentId, isFavorite));
};

const subscribeFavoriteChange = (listener: FavoriteChangeListener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

type UseFavoriteProps = {
  contentId: string;
  initialIsFavorite?: boolean;
};

export const useFavorite = ({ contentId, initialIsFavorite = false }: UseFavoriteProps) => {
  const tShared = useTranslations();
  const { notify } = useNotification();
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
  const [isLoading, setIsLoading] = useState(false);

  const { execute: executePutContentsFavorites } = putContentsFavoritesCall.hook();
  const { execute: executeDeleteContentsFavorites } = deleteContentsFavoritesCall.hook();

  const toggleFavorite = useCallback(() => {
    if (isLoading || !contentId) return;

    setIsLoading(true);

    if (isFavorite) {
      executeDeleteContentsFavorites({
        contentId,
        successSaga: () => {
          setIsFavorite(false);
          setIsLoading(false);
          emitFavoriteChange(contentId, false);
        },
        errorSaga: () => {
          notify(tShared('errors.genericApiError'), 'error');
          setIsLoading(false);
        },
      });
      return;
    }

    executePutContentsFavorites({
      contentId,
      successSaga: () => {
        setIsFavorite(true);
        setIsLoading(false);
        emitFavoriteChange(contentId, true);
      },
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
        setIsLoading(false);
      },
    });
  }, [
    isFavorite,
    contentId,
    executePutContentsFavorites,
    executeDeleteContentsFavorites,
    tShared,
    notify,
    isLoading,
  ]);

  // Subscribe to favorite changes from other components
  useEffect(() => {
    const unsubscribe = subscribeFavoriteChange((changedContentId, newIsFavorite) => {
      if (changedContentId === contentId) {
        setIsFavorite(newIsFavorite);
      }
    });

    return () => {
      unsubscribe(); // ignore the boolean
    };
  }, [contentId]);

  // Sync initial value when it changes
  useEffect(() => {
    setIsFavorite(initialIsFavorite);
  }, [initialIsFavorite]);

  return {
    isFavorite,
    isLoading,
    toggleFavorite,
  };
};
